import { Controller } from "@hotwired/stimulus"
import { Loggable } from "./concerns/loggable"
import Sortable from "sortablejs"

// Minimal Block Editor (MVP)
// - soporta bloques básicos: title, paragraph, button, divider, spacer
// - insertar, editar inline, reordenar, duplicar, eliminar, ocultar/mostrar
// - autosave con debounce 800ms a endpoint PATCH
// - undo/redo en sesión
export default class extends Controller {
  static targets = ["list", "status"]
  static values = {
    updateUrl: String,
    content: Object,
    types: Array,
    addBlockLabel: String
  }

  initialize() {
    Loggable(this, { debug: true })
  }

  connect() {
    this.blocks = (this.contentValue && this.contentValue.blocks) || []
    this.history = []
    this.future = []
    this.debouncedSave = this.debounce(this.autosave.bind(this), 800)
    this.render()
    this.bindKeys()
  }

  // undo ctrl+z, redo ctrl+shift+z
  bindKeys() {
    this.keyHandler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) { this.redo() } else { this.undo() }
      }
    }
    document.addEventListener("keydown", this.keyHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keyHandler)
  }

  // Render UI
  render() {
    this.listTarget.innerHTML = ""
    this.blocks.forEach((blk, index) => {
      this.listTarget.appendChild(this.renderItem(blk, index))
    })
    // Agregar botón + al final
    const addBar = document.createElement("div")
    addBar.className = "d-flex gap-2 my-2"
    addBar.appendChild(this.addMenu())
    this.listTarget.appendChild(addBar)

    this.setupSortable()
  }

  setupSortable(){
    if (!this.sortable) {
      this.sortable = new Sortable(this.listTarget, {
        animation: 300,
        handle: ".card-header",
        ghostClass: "bg-light",
        onEnd: (e) => {
          this.onReorder()
        }
      })
    }
  }

  renderItem(blk, index) {
    const wrapper = document.createElement("div")
    wrapper.className = "card mb-2"
    wrapper.dataset.index = index

    const header = document.createElement("div")
    header.className = "card-header d-flex justify-content-between align-items-center"
    const typeName = this.typesValue.find(([type]) => type === blk.type)?.[1] || blk.type
    header.innerHTML = `
      <div class="text-muted small">${typeName}</div>
      <div class="btn-group">
        <button type="button" class="btn btn-sm btn-outline-secondary" data-action="click->block-editor#duplicate" data-index="${index}"><i class="bi bi-files"></i></button>
        <button type="button" class="btn btn-sm btn-outline-secondary" data-action="click->block-editor#toggleHidden" data-index="${index}">${blk.hidden ? '<i class="bi bi-eye-slash"></i>' : '<i class="bi bi-eye"></i>'}</button>
        <button type="button" class="btn btn-sm btn-outline-danger" data-action="click->block-editor#remove" data-index="${index}"><i class="bi bi-trash"></i></button>
      </div>`

    const body = document.createElement("div")
    body.className = "card-body"
    body.appendChild(this.renderEditor(blk, index))

    wrapper.appendChild(header)
    wrapper.appendChild(body)
    return wrapper
  }

  renderEditor(blk, index) {
    const node = this.buildEditorNode(blk.type)
    if (!node) {
      const c = document.createElement("div")
      c.dataset.index = index
      c.innerHTML = `<div class="text-warning small">Tipo no soportado en el editor (MVP): ${blk.type}</div>`
      return c
    }
    this.populateEditorNode(node, blk, index)
    return node
  }

  buildEditorNode(type) {
    const tpl = document.querySelector(`template[data-cb-editor="${type}"]`)
    if (!tpl) return null
    const fragment = tpl.content.cloneNode(true)
    const node = fragment.firstElementChild || fragment
    return node
  }

  populateEditorNode(node, blk, index) {
    node.dataset.index = index
    const props = blk.props || {}
    node.querySelectorAll('[data-field]').forEach((el) => {
      el.dataset.index = index
      const field = el.getAttribute('data-field')
      switch (field) {
        case 'text':
        case 'url':
          el.value = props[field] || ''
          break
        case 'tag':
        case 'align':
        case 'style':
        case 'size':
          if (props[field]) el.value = props[field]
          break
        case 'url_type':
          el.value = (props['url_type'] || 'custom')
          break
        case 'html':
          el.innerHTML = props.html || ''
          break
      }
    })
    // Inicializar visibilidad del input de URL para botones
    if (node.matches('[data-editor="button"]')) {
      this.updateButtonUrlVisibility(node)
    }
  }

  addMenu() {
    const group = document.createElement("div")
    group.className = "btn-group"
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "btn btn-sm btn-primary dropdown-toggle"
    btn.dataset.bsToggle = "dropdown"
    btn.innerText = this.addBlockLabelValue || "+"
    const menu = document.createElement("ul")
    menu.className = "dropdown-menu"
    this.typesValue.forEach(([type, label]) => {
      const li = document.createElement("li")
      const a = document.createElement("a")
      a.className = "dropdown-item"
      a.href = "#"
      a.innerText = label || type
      a.addEventListener("click", (e) => { e.preventDefault(); this.insert(type) })
      li.appendChild(a)
      menu.appendChild(li)
    })
    group.appendChild(btn)
    group.appendChild(menu)
    return group
  }

  insert(type) {
    const blk = this.newBlock(type)
    this.pushHistory()
    this.blocks.push(blk)
    this.render()
    this.debouncedSave()
  }

  newBlock(type) {
    switch (type) {
      case "title":
        return {type, props: {text: "Nuevo título", tag: "h2", align: "start"}}
      case "paragraph":
        return {type, props: {html: "<p>Nuevo párrafo</p>", align: "start"}}
      case "html":
        return {type, props: {
          html: `<div class="ratio ratio-16x9"><iframe width=\"560\" height=\"315\" src=\"https://www.youtube.com/embed/quCUffdBN7s?si=uBNroLD8kJHvT7sD\" title=\"YouTube video player\" frameborder=\"0\" allow=\"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share\" referrerpolicy=\"strict-origin-when-cross-origin\" allowfullscreen></iframe></div>`,
          align: "start"}
        }
      case "button":
        return {type, props: {text: "Botón", url_type: "payments_section", url: "#", style: "primary", size: "md"}}
      case "divider":
        return {type, props: {}}
      case "spacer":
        return {type, props: {size: "20"}}
      default:
        return {type, props: {}}
    }
  }

  // CRUD acciones
  remove(e) {
    const index = parseInt(e.currentTarget.dataset.index)
    this.pushHistory()
    this.blocks.splice(index, 1)
    this.render()
    this.debouncedSave()
  }

  duplicate(e) {
    const index = parseInt(e.currentTarget.dataset.index)
    this.pushHistory()
    this.blocks.splice(index + 1, 0, JSON.parse(JSON.stringify(this.blocks[index])))
    this.render()
    this.debouncedSave()
  }

  toggleHidden(e) {
    const index = parseInt(e.currentTarget.dataset.index)
    this.pushHistory()
    this.blocks[index].hidden = !this.blocks[index].hidden
    this.render()
    this.debouncedSave()
  }

  onReorder() {
    this.log("onReorder")
    const newOrder = []
    this.listTarget.querySelectorAll('.card').forEach(card => {
      const idx = parseInt(card.dataset.index)
      if (!isNaN(idx)) newOrder.push(this.blocks[idx])
    })
    this.pushHistory()
    this.blocks = newOrder
    this.render()
    this.debouncedSave()
  }

  // Edit handlers
  onTitleText(e) { this.updateProp(e, (p, v) => p.text = v) }
  onTitleTag(e) { this.updateProp(e, (p, v) => p.tag = v) }
  onAlign(e)     { this.updateProp(e, (p, v) => p.align = v) }
  onParagraphHtml(e) { this.updateProp(e, (p, v) => p.html = e.currentTarget.innerHTML) }
  onHtml(e) { this.updateProp(e, (p, v) => p.html = v) }
  onButtonText(e) { this.updateProp(e, (p, v) => p.text = v) }
  onButtonUrl(e) { this.updateProp(e, (p, v) => p.url = v) }
  onButtonUrlType(e) {
    this.updateProp(e, (p, v) => p.url_type = v)
    // Toggle URL input visibility for this block
    const node = e.currentTarget.closest('[data-editor="button"]')
    if (node) this.updateButtonUrlVisibility(node)
  }
  onButtonStyle(e) { this.updateProp(e, (p, v) => p.style = v) }
  onButtonSize(e) { this.updateProp(e, (p, v) => p.size = v) }
  onSpacerSize(e) { this.updateProp(e, (p, v) => p.size = v) }

  updateProp(e, setter) {
    const index = parseInt(e.currentTarget.dataset.index)
    const value = e.currentTarget.value
    this.pushHistory()
    const blk = this.blocks[index]
    blk.props = blk.props || {}
    setter(blk.props, value)
    this.debouncedSave()
  }

  // Mostrar/ocultar el input de URL según url_type
  updateButtonUrlVisibility(node) {
    const select = node.querySelector('[data-field="url_type"]')
    const container = node.querySelector('[data-role="url-input-container"]')
    if (!container || !select) return
    if ((select.value || '').toString() === 'payments_section') {
      container.classList.add('d-none')
    } else {
      container.classList.remove('d-none')
    }
  }

  // Undo/Redo
  pushHistory() {
    this.history.push(JSON.stringify(this.blocks))
    this.future = []
  }

  undo() {
    if (this.history.length === 0) return
    this.future.push(JSON.stringify(this.blocks))
    this.blocks = JSON.parse(this.history.pop())
    this.render()
  }

  redo() {
    if (this.future.length === 0) return
    this.history.push(JSON.stringify(this.blocks))
    this.blocks = JSON.parse(this.future.pop())
    this.render()
  }

  // Saving
  autosave() {
    const payload = { schema_version: 1, blocks: this.blocks }
    this.log("autosaving...", payload)
    this.setStatus("guardando…")
    fetch(this.updateUrlValue, {
      method: "PATCH",
      headers: this.headers(),
      body: JSON.stringify({ content_blocks: payload })
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        this.setStatus(`error: ${(data.errors || []).join("; ")}`)
      } else {
        this.setStatus("guardado")
      }
    }).catch((err) => {
      this.setStatus("error")
    })
  }

  headers() {
    const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
      'Accept': 'application/json'
    }
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  // utils
  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  escape(str) {
    return (str || "").toString().replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
